import React, { forwardRef } from 'react';

export const Input = forwardRef(({ 
  type = 'text',
  size = 'md',
  variant = 'default',
  disabled = false,
  error = false,
  label,
  placeholder,
  helperText,
  errorMessage,
  className = '',
  ...props 
}, ref) => {
  const baseClasses = 'w-full border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    default: 'bg-background border-border focus:border-primary focus:ring-primary',
    filled: 'bg-secondary border-transparent focus:border-primary focus:ring-primary',
    outline: 'bg-transparent border-2 border-border focus:border-primary focus:ring-primary'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm rounded-md',
    md: 'px-4 py-2 text-base rounded-lg',
    lg: 'px-4 py-3 text-lg rounded-lg'
  };
  
  const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';
  const variantClass = variants[variant] || variants.default;
  const sizeClass = sizes[size] || sizes.md;
  
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-text-primary mb-2">
          {label}
        </label>
      )}
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        placeholder={placeholder}
        className={`${baseClasses} ${variantClass} ${sizeClass} ${errorClasses} ${className}`}
        {...props}
      />
      {(helperText || errorMessage) && (
        <p className={`mt-1 text-sm ${error ? 'text-red-600' : 'text-text-muted'}`}>
          {error ? errorMessage : helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export function Textarea({ 
  size = 'md',
  variant = 'default',
  disabled = false,
  error = false,
  label,
  placeholder,
  helperText,
  errorMessage,
  rows = 4,
  className = '',
  ...props 
}) {
  const baseClasses = 'w-full border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed resize-vertical';
  
  const variants = {
    default: 'bg-background border-border focus:border-primary focus:ring-primary',
    filled: 'bg-secondary border-transparent focus:border-primary focus:ring-primary',
    outline: 'bg-transparent border-2 border-border focus:border-primary focus:ring-primary'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm rounded-md',
    md: 'px-4 py-2 text-base rounded-lg',
    lg: 'px-4 py-3 text-lg rounded-lg'
  };
  
  const errorClasses = error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '';
  const variantClass = variants[variant] || variants.default;
  const sizeClass = sizes[size] || sizes.md;
  
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-text-primary mb-2">
          {label}
        </label>
      )}
      <textarea
        disabled={disabled}
        placeholder={placeholder}
        rows={rows}
        className={`${baseClasses} ${variantClass} ${sizeClass} ${errorClasses} ${className}`}
        {...props}
      />
      {(helperText || errorMessage) && (
        <p className={`mt-1 text-sm ${error ? 'text-red-600' : 'text-text-muted'}`}>
          {error ? errorMessage : helperText}
        </p>
      )}
    </div>
  );
}

export default Input;
